import { useMemo } from "react";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TableSkeleton, ListSkeleton } from "../feedback/Skeletons";

/**
 * Responsive table. Renders as a proper table on md+ and as a stacked
 * card list below, so it stays usable on phones without horizontal scroll.
 *
 * column shape:
 *   {
 *     id:        string (required, unique)
 *     label:     string — header label
 *     render:    (row, index) => ReactNode  — cell body
 *     align:     'left' | 'right' | 'center'
 *     width:     CSS width (desktop only)
 *     primary:   boolean — used as the title in mobile card view
 *     hideOnMobile: boolean
 *     hideLabelOnMobile: boolean — for trailing actions, etc.
 *   }
 */
export default function DataTable({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  emptyState,
  stickyHeader = true,
  sx,
}) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const getKey = useMemo(() => {
    if (typeof rowKey === "function") return rowKey;
    return (row, index) => row?.[rowKey] ?? index;
  }, [rowKey]);

  if (loading) {
    return isCompact ? (
      <ListSkeleton rows={5} />
    ) : (
      <TableSkeleton rows={6} columns={columns.length} />
    );
  }

  if (!rows || rows.length === 0) {
    return emptyState ?? null;
  }

  if (isCompact) {
    return (
      <Stack sx={sx}>
        {rows.map((row, index) => {
          const primaryCol = columns.find((c) => c.primary);
          const otherCols = columns.filter(
            (c) => !c.primary && !c.hideOnMobile,
          );
          return (
            <Box
              key={getKey(row, index)}
              onClick={onRowClick ? () => onRowClick(row, index) : undefined}
              sx={{
                px: 2,
                py: 2,
                borderBottom: 1,
                borderColor: "divider",
                cursor: onRowClick ? "pointer" : "default",
                "&:last-of-type": { borderBottom: 0 },
                "&:hover": onRowClick
                  ? { bgcolor: "background.default" }
                  : undefined,
              }}
            >
              {primaryCol && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {primaryCol.render(row, index)}
                  </Typography>
                </Box>
              )}
              <Stack spacing={0.75}>
                {otherCols.map((col) => {
                  const content = col.render(row, index);
                  if (content == null || content === "") return null;
                  return (
                    <Stack
                      key={col.id}
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      {!col.hideLabelOnMobile && col.label && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ pt: 0.25, flexShrink: 0 }}
                        >
                          {col.label}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          textAlign: "right",
                          minWidth: 0,
                          flex: 1,
                          fontSize: "0.875rem",
                        }}
                      >
                        {content}
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  }

  return (
    <Box sx={{ overflowX: "auto", ...sx }}>
      <Table stickyHeader={stickyHeader} sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align ?? "left"}
                sx={{
                  width: col.width,
                  whiteSpace: "nowrap",
                  bgcolor: "background.paper",
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={getKey(row, index)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row, index) : undefined}
              sx={{
                cursor: onRowClick ? "pointer" : "default",
              }}
            >
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align ?? "left"}>
                  {col.render(row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
